import { useState } from "react";
import { Link } from "react-router-dom";

const smallBizPackages = [
  {
    name: "Essential Launch Bundle",
    tag: "New & emerging businesses",
    features: ["LLC / Corp Registration Support", "Business License Guidance", "Bank Account Setup Support", "Branding Advisory Session", "SWOT Analysis"],
    featured: false,
  },
  {
    name: "Growth Strategy Bundle",
    tag: "Businesses ready to scale",
    features: ["Full Business Plan Development", "Investor Pitch Deck", "Market Research Report", "Financial Projections", "90-Day Action Plan"],
    featured: true,
  },
  {
    name: "Operations Excellence",
    tag: "Businesses needing structure",
    features: ["SOP Manual Development", "Employee Handbook", "Pricing Matrix Design", "Process Documentation", "Workflow Optimization"],
    featured: false,
  },
  {
    name: "Protection & Funding",
    tag: "Businesses seeking capital & coverage",
    features: ["Risk Assessment", "Insurance Review & Placement", "Funding Readiness Assessment", "Grant Writing Assistance", "Financial Model Development"],
    featured: false,
  },
];

const enterprisePackages = [
  {
    name: "Corporate Foundation",
    tag: "Established businesses",
    features: ["Advanced Legal Structuring", "Strategic Growth Planning", "Executive Coaching", "Enterprise Risk Assessment", "Data Analytics Setup"],
    featured: false,
  },
  {
    name: "Corporate Growth",
    tag: "Scaling businesses",
    features: ["Market Expansion Strategies", "Mergers & Acquisitions Advisory", "Financial Modeling", "KPI Dashboard Implementation", "Team Training Programs"],
    featured: true,
  },
  {
    name: "Corporate Transformation",
    tag: "Businesses in transition",
    features: ["Business Transformation Strategy", "Succession Planning", "Business Valuation", "Change Management", "Leadership Development"],
    featured: false,
  },
  {
    name: "Enterprise Wellness",
    tag: "Organizations investing in people",
    features: ["Wellness Warriors Program", "Executive Wellness Coaching", "Team Resilience Workshops", "Burnout Prevention Strategy", "Work-Life Integration Planning"],
    featured: false,
  },
];

const faqs = [
  { q: "Is the initial consultation really free?", a: "Yes, your first strategy session with Paradigm is completely free. We believe in building relationships first and understanding your needs before discussing any engagement." },
  { q: "What is the process after booking a call?", a: "After booking, you'll receive a confirmation email. During the call, we'll discuss your business, challenges, and goals. From there, we'll recommend the best path forward — no pressure, no obligation." },
  { q: "Do you work with businesses outside of Florida?", a: "Absolutely. We are nationwide and international. While based in Lakeland, Florida, we serve entrepreneurs across the United States and internationally through virtual consultations." },
  { q: "How are your services priced?", a: "Rather than publishing generic rates, we customize proposals based on your specific needs and goals. Every business is different — your investment should reflect that. Schedule a call for a custom quote." },
  { q: "Can I choose services from different programs?", a: "Yes! Our programs are designed to be flexible. You can mix and match services from Business Logic, IAFF, and Paradigm Shift Architects based on your needs." },
  { q: "What industries do you specialize in?", a: "We work with entrepreneurs across all industries. Our focus is on the fundamentals of business success — strategy, planning, protection, and execution — which apply universally." },
  { q: "How long does it take to see results?", a: "Results vary based on your starting point and goals. Some clients see quick wins within weeks, while comprehensive business transformations may take several months." },
  { q: "Do you offer ongoing support or just one-time projects?", a: "Both! We offer one-time engagements as well as ongoing advisory relationships. Many of our clients choose ongoing partnerships for continuous growth and support." },
  { q: "What makes Paradigm different from other consultants?", a: "We're an All-In-One Resource Center. Instead of juggling 5-10 different vendors, you get one relationship, one team, and comprehensive support across all business areas." },
  { q: "How do I get started?", a: "Simply click 'Book a Call' to schedule your free strategy session. We'll take it from there — no pressure, no obligation, just genuine support for your business journey." },
];

const steps = [
  { num: "01", title: "Book a Call", desc: "Schedule your free strategy session — 30 minutes that could change your business." },
  { num: "02", title: "Discovery", desc: "We learn about your business, challenges, goals, and current situation." },
  { num: "03", title: "Custom Proposal", desc: "We design a tailored plan with the exact services and support you need." },
  { num: "04", title: "Implementation", desc: "We execute together — providing expertise, tools, and ongoing support." },
];

export const BookPage = () => {
  const [activeTab, setActiveTab] = useState("small");
  const [openFaq, setOpenFaq] = useState(null);
  const packages = activeTab === "small" ? smallBizPackages : enterprisePackages;

  return (
    <main data-testid="book-page">
      {/* PAGE HERO */}
      <div className="pg-page-hero">
        <div className="pg-container">
          <span className="pg-label">Packages & FAQ</span>
          <h1>Choose the Right Path</h1>
          <p>4 Small Business + 4 Enterprise packages, 10 FAQ items, and easy booking</p>
        </div>
      </div>

      {/* PACKAGES */}
      <section className="pg-section">
        <div className="pg-container">
          <div className="pg-text-center">
            <span className="pg-label">Our Packages</span>
            <h2>Tailored Solutions for Every Stage</h2>
            <div className="pg-divider" />
          </div>

          <div className="pg-pkg-tabs" data-testid="package-tabs">
            <button className={`pg-pkg-tab ${activeTab === "small" ? "active" : ""}`} onClick={() => setActiveTab("small")}>Small Business</button>
            <button className={`pg-pkg-tab ${activeTab === "enterprise" ? "active" : ""}`} onClick={() => setActiveTab("enterprise")}>Enterprise</button>
          </div>

          <div className="pg-pkg-grid">
            {packages.map((pkg, i) => (
              <div key={pkg.name} className={`pg-pkg-card ${pkg.featured ? "featured" : ""}`} data-testid={`package-card-${i}`}>
                <div className="pg-pkg-name">{pkg.name}</div>
                <div className="pg-pkg-tag">{pkg.tag}</div>
                <div className="pg-pkg-inc">Includes:</div>
                <ul className="pg-pkg-feats">
                  {pkg.features.map((f) => <li key={f}>{f}</li>)}
                </ul>
                <Link to="/consultation" className="pg-btn pg-btn-primary" style={{ justifyContent: "center", marginTop: "auto" }}>Get Started</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="pg-section pg-section-light">
        <div className="pg-container">
          <div className="pg-text-center">
            <span className="pg-label">How It Works</span>
            <h2>Four Simple Steps to Get Started</h2>
            <div className="pg-divider" />
          </div>
          <div className="pg-steps-grid">
            {steps.map((s, i) => (
              <div className="pg-step" key={s.title}>
                <div className="pg-step-num">{s.num}</div>
                <h4 style={{ color: "var(--black)", marginBottom: ".3rem" }}>{s.title}</h4>
                <p style={{ color: "var(--muted)", fontSize: ".88rem", margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pg-section">
        <div className="pg-container">
          <div className="pg-text-center">
            <span className="pg-label">Frequently Asked Questions</span>
            <h2>Get Your Questions Answered</h2>
            <div className="pg-divider" />
          </div>
          <div className="pg-faq-wrap">
            {faqs.map((faq, i) => (
              <div key={faq.q} className={`pg-faq-item ${openFaq === i ? "open" : ""}`} data-testid={`faq-item-${i}`}>
                <button className="pg-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {faq.q}
                  <span className="pg-faq-icon">+</span>
                </button>
                <div className="pg-faq-ans">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section className="pg-section pg-section-light" id="book-form" data-testid="booking-section">
        <div className="pg-container">
          <div className="pg-text-center">
            <span className="pg-label">Schedule Your Call</span>
            <h2>Book Your Free Strategy Session</h2>
            <div className="pg-divider" />
            <p className="pg-intro">Select a time that works for you. Your initial consultation is completely free — no obligation.</p>
          </div>

          <div style={{ maxWidth: 700, margin: "0 auto", background: "#fff", borderRadius: 16, boxShadow: "var(--shadow)", overflow: "hidden" }}>
            <iframe
              src="https://api.leadconnectorhq.com/widget/booking/eD3fzSqRNf9sCR0R09gH"
              style={{ width: "100%", minHeight: "1100px", border: "none" }}
              scrolling="yes"
              id="eD3fzSqRNf9sCR0R09gH_1775502017528"
              title="Book a Consultation"
              data-testid="ghl-booking-iframe"
            />
          </div>

          <div style={{ textAlign: "center", marginTop: "2rem" }}>
            <p style={{ color: "var(--muted)", fontSize: ".9rem" }}>Prefer to reach out directly?</p>
            <div style={{ display: "flex", gap: "1rem", justifyContent: "center", marginTop: ".8rem", flexWrap: "wrap" }}>
              <a href="tel:+18635851412" className="pg-btn pg-btn-secondary" data-testid="book-phone-link">
                <i className="fas fa-phone" /> (863) 585-1412
              </a>
              <a href="mailto:info@financialfreedomdelivered.com" className="pg-btn pg-btn-secondary" data-testid="book-email-link">
                <i className="fas fa-envelope" /> Email Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BookPage;
